import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, getClues, runFullArray } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { SceneReconstruction } from "@/components/SceneReconstruction";
import { GhostInterview } from "@/components/GhostInterview";
import { BehavioralPanel } from "@/components/BehavioralPanel";
import { LegalCustodyPanel } from "@/components/LegalCustodyPanel";
import { SpecialistLibrary } from "@/components/SpecialistLibrary";
import { ArrowLeft, Boxes, Ghost, Loader2, Brain, Scale, Cpu, Zap } from "lucide-react";
import { toast } from "sonner";

function SectionHeader({ icon: Icon, title, tag, accent = "text-monk-blue" }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <Icon className={`w-4 h-4 ${accent}`} />
      <div className="overline">{title}</div>
      <div className="h-px flex-1 bg-white/10" />
      <span className="font-heading font-700 text-sm tracking-tight text-neutral-400">{tag}</span>
    </div>
  );
}

export default function Simulation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = () => api.get(`/cases/${id}`).then((r) => setData(r.data))
    .catch(() => { toast.error("This case file could not be loaded"); nav(`/case/${id}`); });
  const loadPulse = () => getClues(id).then((r) => setPulse(r.pulse || null)).catch(() => {});
  useEffect(() => { load(); loadPulse(); }, [id]);

  // While the full engine array (or per-evidence intake) is running, refresh the case
  // record + pulse so every engine result appears the moment it lands — no manual runs.
  const running = !!pulse?.running || (pulse?.evidence_running || 0) > 0;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => { load(); loadPulse(); }, 4000);
    return () => clearInterval(t);
  }, [running]);

  const rerun = async () => {
    setStarting(true);
    try {
      await runFullArray(id);
      toast.success("Monk is re-running the full engine array — findings update live.");
      await loadPulse();
    } catch { toast.error("Could not start the full engine array"); }
    finally { setStarting(false); }
  };

  if (!data) return (
    <AppShell>
      <div className="flex items-center gap-2 font-mono text-monk-blue text-sm" data-testid="simulation-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> LOADING CASE INTELLIGENCE…
      </div>
    </AppShell>
  );

  const { case: c } = data;
  const done = pulse?.done || 0;
  const total = pulse?.total || 0;

  return (
    <AppShell>
      <button onClick={() => nav(`/case/${id}`)} data-testid="back-to-case-btn"
        className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Case File
      </button>

      {/* Header */}
      <div className="bg-monk-panel border border-white/10 rounded-md p-6 mb-8 grid-noise" data-testid="simulation-header">
        <div className="overline mb-2">Case Intelligence · Behavioral · Legal · Specialist Library · Simulation</div>
        <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tighter leading-none mb-3">CASE INTELLIGENCE</h1>
        <p className="text-neutral-300 text-sm max-w-3xl leading-relaxed">
          The case-wide reasoning surface. The full engine array — offender profile, geography, timeline, the forensic
          specialist library, cross-case links, an adversarial stress test, and scene reconstruction — fires automatically
          as evidence lands. Every inference here is a hypothesis drawn from the case record; weigh it against the hard evidence.
        </p>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[12px] text-neutral-500 font-mono">
            <span className="text-monk-blue">{c.title}</span>
            <span>·</span>
            <span>{c.case_type?.replace("_", " ")}</span>
          </div>
          <button onClick={rerun} disabled={starting || running} data-testid="rerun-full-array-btn"
            className="flex items-center gap-2 bg-monk-blue text-white text-sm font-medium rounded-sm px-4 py-2.5 hover:bg-monk-blue/90 disabled:opacity-60 transition-colors">
            {running
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Running Array{total ? ` · ${done}/${total}` : ""}…</>
              : <><Zap className="w-4 h-4" /> Re-run Full Array</>}
          </button>
        </div>
      </div>

      {/* Behavioral & Geographic Intelligence — Milestone 3 */}
      <div className="mb-10" data-testid="simulation-behavioral-section">
        <SectionHeader icon={Brain} title="Behavioral & Geographic Intelligence" tag="§17 · The Who / Where / When" />
        <BehavioralPanel caseData={c} />
      </div>

      {/* Legal & Custody — Milestone 4 */}
      <div className="mb-10" data-testid="simulation-legal-section">
        <SectionHeader icon={Scale} title="Legal, Custody & Adversarial Review" tag="§8 · §18 · §23" accent="text-monk-warn" />
        <LegalCustodyPanel caseId={id} caseData={c} onUpdated={load} />
      </div>

      {/* Forensic Specialist Library — Milestone 4 */}
      <div className="mb-10" data-testid="simulation-specialist-section">
        <SectionHeader icon={Cpu} title="Forensic Specialist Library" tag="Engine Registry" />
        <SpecialistLibrary caseData={c} />
      </div>

      {/* Scene Reconstruction */}
      <div className="mb-10" data-testid="simulation-scene-section">
        <SectionHeader icon={Boxes} title="Forensic Scene Reconstruction" tag="§10 · The Sequence" />
        <SceneReconstruction caseData={c} />
      </div>

      {/* Ghost Interview */}
      <div data-testid="simulation-ghost-section">
        <SectionHeader icon={Ghost} title="Synthetic Killer · Ghost Interview" tag="§15 · The Mind" accent="text-monk-critical" />
        <GhostInterview caseId={id} />
      </div>
    </AppShell>
  );
}
